import React, { useEffect, useMemo, useRef, useState } from 'react';
import { typesetWhenReady } from './RichText';
import SketchWhiteboard from './SketchWhiteboard';
import './InterviewSitting.css';

/**
 * Exhibit + working-space tabs, per the mockup's board layout. Which
 * working-space control shows (sketch vs numeric) comes from `expects`
 * (interview_runtime.py's _expects_payload) rather than from re-deriving
 * it client-side — the backend already knows what the current rung's
 * sketch/computation fields hold; this only decides how to render that.
 *
 * questionExhibit/rungExhibit are the exact sanitised shapes
 * _current_exhibits() returns: {exhibit_text, exhibit_is_html} | null. HTML
 * exhibits are already sanitised server-side (exhibit_sanitizer.py) before
 * they ever reach this component, so rendering via dangerouslySetInnerHTML
 * here is the same trust boundary that decision already crossed, not a new
 * one opened here.
 */
export default function InterviewBoard({
  questionExhibit,
  rungExhibit,
  expects,
  sketchPrompt,
  computationUnits,
  onSubmitSketch,
  onSubmitComputation,
  sketchBusy,
  sketchResult,
  computationBusy,
  computationResult,
}) {
  const hasExhibit = Boolean(questionExhibit || rungExhibit);
  const hasWork = Boolean(expects?.sketch || expects?.computation);
  // What the current rung needs comes first; failing that, whatever the
  // examiner is showing; failing that, the scratch pad rather than an empty
  // exhibit placeholder.
  const preferredTab = hasWork || !hasExhibit ? 'work' : 'exhibit';
  const [tab, setTab] = useState(preferredTab);
  const [computationAnswer, setComputationAnswer] = useState('');

  // A useState initializer only runs once - it doesn't re-fire when a new
  // turn changes what the CURRENT rung needs, so switching to a sketch
  // rung after a plain one left the candidate stranded on the Exhibit tab
  // with no visible reason the flag appeared. Re-picks the tab whenever
  // what's actually available changes, mirroring the mockup's own
  // runStation() re-setting the pane on every new station.
  useEffect(() => {
    setTab(preferredTab);
    setComputationAnswer('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expects?.sketch, expects?.computation, questionExhibit, rungExhibit]);

  const submitComputation = (e) => {
    e.preventDefault();
    if (!computationAnswer.trim()) return;
    onSubmitComputation(computationAnswer.trim());
  };

  return (
    <div className={`is-board${!hasExhibit && !hasWork ? ' is-board--idle' : ''}`}>
      <div className="is-board-tabs">
        <button
          type="button"
          className={`is-board-tab${tab === 'exhibit' ? ' is-board-tab--active' : ''}`}
          onClick={() => setTab('exhibit')}
        >
          Exhibit
        </button>
        {/* Always offered. A real interview gives you paper for your working
            whether or not anything on it is marked, and the corpus asks for
            drawings the ladder doesn't score - CAMNS-0017's second
            interview opens "Draw three different structures" with no sketch
            on any of its rungs. Hiding the tab left nothing to draw on. */}
        <button
          type="button"
          className={`is-board-tab${tab === 'work' ? ' is-board-tab--active' : ''}`}
          onClick={() => setTab('work')}
        >
          Working space
          {hasWork && <span className="is-board-tab-flag" />}
        </button>
      </div>

      <div className="is-board-pane">
        {tab === 'exhibit' && (
          hasExhibit ? (
            <div className="is-exhibit-scroll">
              {questionExhibit && (
                <ExhibitCard exhibit={questionExhibit} kicker="This question" />
              )}
              {rungExhibit && (
                <ExhibitCard exhibit={rungExhibit} kicker="This step" />
              )}
            </div>
          ) : (
            <div className="is-board-empty">
              Charts, tables and extracts appear here when the examiner brings them in.
            </div>
          )
        )}

        {/* Kept mounted and hidden rather than unmounted, so switching to the
            exhibit and back doesn't wipe what the candidate has drawn. */}
        <div className="is-work-pane" hidden={tab !== 'work'}>
          {expects?.sketch && (
            <SketchWhiteboard
              key={`sketch:${sketchPrompt || ''}`}
              what={sketchPrompt}
              onSubmit={onSubmitSketch}
              busy={sketchBusy}
            />
          )}
          {expects?.computation && (
            <form className="is-computation" onSubmit={submitComputation}>
              <label className="is-computation-label" htmlFor="is-computation-input">
                Enter your answer{computationUnits ? ` (${computationUnits})` : ''}
              </label>
              <div className="is-computation-row">
                <input
                  id="is-computation-input"
                  className="is-computation-input"
                  type="text"
                  inputMode="decimal"
                  value={computationAnswer}
                  onChange={(e) => setComputationAnswer(e.target.value)}
                  disabled={computationBusy}
                  placeholder="e.g. 42 or 3.14"
                />
                <button type="submit" className="is-btn" disabled={computationBusy || !computationAnswer.trim()}>
                  {computationBusy ? 'Checking…' : 'Check'}
                </button>
              </div>
              {computationResult && (
                <div className={`is-computation-result${computationResult.correct ? ' is-computation-result--correct' : ''}`}>
                  {computationResult.correct
                    ? 'Correct.'
                    : 'Not right yet. Keep working on it, or carry on with your answer.'}
                </div>
              )}
            </form>
          )}
          <div className="is-scratch" hidden={Boolean(expects?.sketch)}>
            <SketchWhiteboard scratch />
          </div>
        </div>
      </div>

      {sketchResult && tab === 'work' && expects?.sketch && (
        <div className={`is-sketch-result${sketchResult.essential_met ? ' is-sketch-result--met' : ''}`}>
          {sketchResult.essential_met
            ? 'Your drawing has the key features.'
            : 'Your drawing may be missing some key features. Redraw it, or carry on with your answer.'}
        </div>
      )}
    </div>
  );
}

export function ExhibitCard({ exhibit, kicker }) {
  const ref = useRef(null);
  // One object per exhibit, for the same reason as RichText's (React 19).
  const inner = useMemo(() => ({ __html: exhibit.exhibit_text }), [exhibit.exhibit_text]);
  // A few exhibits carry maths ($...$) in their labels or captions.
  useEffect(() => {
    if ((exhibit.exhibit_text || '').includes('$')) typesetWhenReady(ref.current);
  }, [exhibit.exhibit_text]);
  return (
    <div className="is-exhibit-card" ref={ref}>
      <div className="is-exhibit-kicker">{kicker}</div>
      {exhibit.exhibit_is_html ? (
        // eslint-disable-next-line react/no-danger
        <div className="mathjax-process" dangerouslySetInnerHTML={inner} />
      ) : (
        <p className="is-exhibit-text mathjax-process">{exhibit.exhibit_text}</p>
      )}
    </div>
  );
}
